import React, { useState, useEffect } from 'react';
import { Users, TrendingUp, Clock, XCircle, Eye, Edit, Trash2, CheckCircle, AlertCircle, EyeOff, TicketCheck, MessageSquare, ChevronDown, Activity, Sparkles, BriefcaseBusiness } from 'lucide-react';
import API_URL from '../config/api';
import MetricsPanel from './MetricsPanel';
import api from '../utils/apiClient';
import AgencyOperations from './AgencyOperations';

// ⭐ Mismo agrupamiento que getVertical() en backend/index.js (2026-08-04):
// restaurantes y tiendas ya no comparten precio ni límite de productos.
// Debe mantenerse en sync manualmente -- no hay un solo lugar de verdad
// compartido entre frontend y backend para esto todavía.
const RETAIL_VERTICAL_CATEGORIES = new Set(['retail', 'pharmacy']);
const getVertical = (category) => (RETAIL_VERTICAL_CATEGORIES.has(category) ? 'tienda' : 'restaurante');

// ⭐ Proveedores de IA (pedido de Angelo, 2026-08-06: "no quiero usar Google
// AI Studio... quiero que se pueda conectar a cualquiera, como si fuera un
// nodo en n8n"). El backend habla el formato genérico OpenAI-compatible
// (ver callChatCompletion en aiReply.js), así que cambiar de proveedor es
// solo cambiar api key + baseUrl + model -- estos presets son solo un
// atajo de UI para no tener que buscar la URL cada vez, "Personalizado"
// cubre cualquier otro proveedor compatible que no esté en la lista.
// DeepSeek va primero y es el default: es la opción más barata para hacer
// pruebas (pedido explícito de Angelo).
const AI_PROVIDER_PRESETS = [
  { id: 'deepseek', label: 'DeepSeek (recomendado, el más barato)', baseUrl: 'https://api.deepseek.com/v1', model: 'deepseek-chat' },
  { id: 'openai', label: 'OpenAI', baseUrl: 'https://api.openai.com/v1', model: 'gpt-4o-mini' },
  { id: 'openrouter', label: 'OpenRouter', baseUrl: 'https://openrouter.ai/api/v1', model: 'openai/gpt-4o-mini' },
  { id: 'groq', label: 'Groq', baseUrl: 'https://api.groq.com/openai/v1', model: 'llama-3.3-70b-versatile' },
  { id: 'together', label: 'Together AI', baseUrl: 'https://api.together.xyz/v1', model: 'meta-llama/Llama-3.3-70B-Instruct-Turbo' },
  { id: 'xai', label: 'xAI (Grok)', baseUrl: 'https://api.x.ai/v1', model: 'grok-2-latest' },
  { id: 'custom', label: 'Personalizado (otro proveedor compatible con OpenAI)', baseUrl: '', model: '' },
];
const DEFAULT_AI_PRESET = AI_PROVIDER_PRESETS[0]; // deepseek

// ⭐ Planes de una cuenta CRM-only (pedido de Angelo, 2026-08-07: "que si es
// categoría CRM... los planes que salgan para mejorarlo, solo de CRM, nada
// de 500 productos"). Mismos 3 tiers que la landing (CRM.jsx) y el registro
// standalone (/api/register-crm en el backend, que hoy solo asigna
// 'emprendedor' -- ia_plus/business existen acá para que el admin pueda
// subir de plan a mano a un cliente ya registrado, aunque el checkout propio
// para esos 2 todavía no esté construido). Mantenerse en sync manualmente con
// CRM.jsx si cambian precios/nombres -- no hay un solo lugar de verdad
// compartido entre landing y admin todavía (mismo caso que RETAIL_VERTICAL_CATEGORIES).
const CRM_PLAN_TIERS = [
  { id: 'emprendedor', label: 'Emprendedor ($29/mes) -- 1 canal WhatsApp, contactos ilimitados, pipeline, workflows' },
  { id: 'ia_plus', label: 'IA Plus ($49/mes) -- Emprendedor + respuestas automáticas por IA (próximamente)' },
  { id: 'business', label: 'Business ($129/mes) -- IA Plus + multi-canal y multi-usuario (próximamente)' },
];

function matchAiPreset(baseUrl) {
  const found = AI_PROVIDER_PRESETS.find(p => p.id !== 'custom' && p.baseUrl === (baseUrl || '').trim());
  return found ? found.id : 'custom';
}

function Dashboard() {
  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, trial: 0, cancelled: 0 });
  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  // ⭐ Ver api key de IA ya guardada (pedido de Angelo, 2026-08-07): el
  // input de abajo es write-only a propósito (nunca se precarga con el
  // valor real, ver openEditModal), así que esto es un canal aparte de
  // "solo lectura bajo demanda" -- no se mete en newClient.crm_ai_api_key
  // para no arriesgar que un guardado accidental la reescriba con lo mismo
  // sin querer. Se resetea al abrir/cerrar el modal, ver openEditModal y
  // closeModal.
  const [revealedAiKey, setRevealedAiKey] = useState(null);
  const [loadingAiKeyReveal, setLoadingAiKeyReveal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showPassword, setShowPassword] = useState(false);
  const [activeTab, setActiveTab] = useState('clients');
  const [tickets, setTickets] = useState([]);
  const [ticketFilter, setTicketFilter] = useState('all');
  const [viewingTicket, setViewingTicket] = useState(null);
  const [ticketAdminNotes, setTicketAdminNotes] = useState('');
  const [newClient, setNewClient] = useState({
    product_type: 'pos',
    name: '',
    email: '',
    phone: '',
    category: 'restaurant',
    plan: 'basico',
    status: 'trial',
    password: '',
    payment_due: '',
    extra_tables: 0,
    extra_products: 0,
    extra_employees: 0,
    crm_enabled: false,
    menu_dinamico_enabled: false,
    menu_estatico_enabled: false,
    crm_ai_enabled: false,
    crm_ai_daily_limit: 20,
    // ⭐ Plan de una cuenta CRM-only (pedido de Angelo, 2026-08-07): campo
    // aparte de `plan` a propósito -- `plan` sigue siendo el de POS
    // (queda en 'basico', irrelevante para estas cuentas, ver comentario en
    // /api/register-crm). Ver CRM_PLAN_TIERS más abajo para las opciones.
    crm_plan_tier: '',
    // ⭐ Write-only (pedido de Angelo, 2026-08-06) -- nunca se precarga con
    // la clave real al editar un cliente (el backend nunca la devuelve en
    // texto plano), así que arranca siempre vacío. Vacío al guardar =
    // "no tocar la clave que ya había" (ver COALESCE en /api/update-client/:id).
    crm_ai_api_key: '',
    // ⭐ NO son secretos (a diferencia de crm_ai_api_key) -- se precargan
    // directo con lo que ya tiene el cliente al editar, ver openEditModal.
    crm_ai_base_url: DEFAULT_AI_PRESET.baseUrl,
    crm_ai_model: DEFAULT_AI_PRESET.model,
    // ⭐ Nodo IA: transcribir audio (pedido de Angelo, 2026-08-07) -- modelo
    // APARTE del de chat de arriba (Whisper y compatibles usan su propio
    // nombre de modelo, ej. "whisper-1"). Opcional: vacío = el backend cae
    // a "whisper-1" solo (ver resolveAiConfig en aiReply.js).
    crm_ai_transcription_model: '',
  });
  const [testingAiKey, setTestingAiKey] = useState(false);
  const [aiKeyTestResult, setAiKeyTestResult] = useState(null); // { ok, error? } | null
  const [aiProviderPreset, setAiProviderPreset] = useState(DEFAULT_AI_PRESET.id);

  const handleAiPresetChange = (presetId) => {
    setAiProviderPreset(presetId);
    setAiKeyTestResult(null);
    const preset = AI_PROVIDER_PRESETS.find(p => p.id === presetId);
    if (preset && presetId !== 'custom') {
      setNewClient(prev => ({ ...prev, crm_ai_base_url: preset.baseUrl, crm_ai_model: preset.model }));
    }
  };

  useEffect(() => {
    loadClients();
    loadStats();
    loadTickets();
  }, []);

  const loadTickets = async () => {
    try {
      const response = await api.get(`/api/tickets`);
      setTickets(response.data);
    } catch (error) {
      console.error('Error al cargar tickets:', error);
    }
  };

  const loadClients = async () => {
    try {
      const response = await api.get(`/api/clients`);
      setClients(response.data);
    } catch (error) {
      console.error('Error al cargar clientes:', error);
    }
  };

  const loadStats = async () => {
    try {
      const response = await api.get(`/api/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error al cargar estadísticas:', error);
    }
  };

  const handleCreateClient = async () => {
    if (!newClient.name || !newClient.email) {
      alert('Por favor completa el nombre y el email');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newClient.email)) {
      alert('Por favor ingresa un email válido');
      return;
    }

    if (!editingClient && (!newClient.password || newClient.password.length < 6)) {
      alert('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    try {
      if (editingClient) {
        // La API key es de solo escritura: el campo queda vacío al editar
        // una cuenta para no exponerla. No enviar ese valor vacío evita que
        // una actualización normal reemplace la clave ya guardada.
        const updatePayload = { ...newClient };
        if (!updatePayload.crm_ai_api_key?.trim()) delete updatePayload.crm_ai_api_key;
        await api.put(`/api/update-client/${editingClient.id}`, updatePayload);
        alert('Cliente actualizado exitosamente');
      } else {
        const response = await api.post(`/api/create-client`, newClient);
        const { credentials } = response.data;
        
        alert(`✅ CLIENTE CREADO EXITOSAMENTE

🔐 CREDENCIALES DE ACCESO AL POS:

📧 Email:      ${credentials.email}
🔑 Contraseña: ${credentials.password}

⚠️ IMPORTANTE: 
Envía estas credenciales al cliente de forma segura.
El cliente usará su EMAIL para iniciar sesión.

📅 El cliente tiene 15 días de prueba gratuita.

🌐 URL de Login: ${process.env.REACT_APP_LANDING_URL || 'http://localhost:3001'}`);
      }
      
      loadClients();
      loadStats();
      closeModal();
    } catch (error) {
      console.error('Error:', error);
      alert(error.response?.data?.error || 'Error al guardar el cliente');
    }
  };

  const handleTestAiKey = async () => {
    if (!newClient.crm_ai_api_key || !newClient.crm_ai_api_key.trim()) return;
    if (!newClient.crm_ai_base_url?.trim() || !newClient.crm_ai_model?.trim()) {
      setAiKeyTestResult({ ok: false, error: 'Completa también la URL base y el modelo antes de probar.' });
      return;
    }
    setTestingAiKey(true);
    setAiKeyTestResult(null);
    try {
      const response = await api.post('/api/admin/test-ai-connection', {
        apiKey: newClient.crm_ai_api_key.trim(),
        baseUrl: newClient.crm_ai_base_url.trim(),
        model: newClient.crm_ai_model.trim(),
      });
      if (response.data.success) {
        setAiKeyTestResult({ ok: true });
      } else {
        setAiKeyTestResult({ ok: false, error: response.data.error || 'La clave no funcionó' });
      }
    } catch (error) {
      setAiKeyTestResult({ ok: false, error: error.response?.data?.error || 'Error al probar la clave' });
    } finally {
      setTestingAiKey(false);
    }
  };

  const handleDeleteClient = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este cliente?')) return;
    
    try {
      await api.delete(`/api/delete-client/${id}`);
      loadClients();
      loadStats();
    } catch (error) {
      console.error('Error al eliminar:', error);
      alert(error.response?.data?.error || 'No se pudo eliminar el cliente. Inténtalo nuevamente.');
    }
  };

  const handleUpdateStatus = async (id, newStatus) => {
    try {
      await api.post(`/api/update-status`, { id, status: newStatus });
      loadClients();
      loadStats();
    } catch (error) {
      console.error('Error al actualizar estado:', error);
    }
  };

  const handleResetPassword = async (client) => {
    if (!window.confirm(`¿Restablecer la contraseña de ${client.name}? La contraseña actual dejará de funcionar.`)) return;
    const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%';
    const random = new Uint32Array(14);
    window.crypto.getRandomValues(random);
    const newPassword = Array.from(random, (value) => alphabet[value % alphabet.length]).join('');
    try {
      const response = await api.post(`/api/admin/client/${client.id}/reset-password`, { new_password: newPassword });
      alert(`✅ ACCESO RESTABLECIDO\n\n📧 Email: ${response.data.email}\n🔑 Contraseña temporal: ${newPassword}\n\nCompártela una sola vez por un canal seguro. MARKETOS no la guardará ni podrá volver a mostrarla.`);
    } catch (error) {
      console.error('Error al restablecer acceso:', error);
      alert(error.response?.data?.error || 'Error al restablecer el acceso');
    }
  };

  const handleViewAiKey = async () => {
    if (!editingClient) return;
    setLoadingAiKeyReveal(true);
    try {
      const response = await api.get(`/api/admin/client/${editingClient.id}/ai-key`);
      // ⭐ '__none__' distinto de null: null = "todavía no se pidió",
      // '__none__' = "se pidió y la cuenta no tiene clave propia guardada
      // (usa la del servidor)" -- si no se distinguen, el botón parece no
      // hacer nada cuando la cuenta usa la clave del servidor.
      setRevealedAiKey(response.data.apiKey || '__none__');
    } catch (error) {
      console.error('Error al obtener la api key de IA:', error);
      setRevealedAiKey('__error__');
    } finally {
      setLoadingAiKeyReveal(false);
    }
  };

  const openEditModal = (client) => {
    setEditingClient(client);
    setRevealedAiKey(null);
    setNewClient({
      product_type: client.product_type || 'pos',
      name: client.name,
      email: client.email,
      phone: client.phone || '',
      category: client.category,
      plan: client.plan,
      status: client.status,
      password: '',
      payment_due: client.payment_due ? client.payment_due.split('T')[0] : '',
      extra_tables: client.extra_tables || 0,
      extra_products: client.extra_products || 0,
      extra_employees: client.extra_employees || 0,
      crm_enabled: Boolean(client.crm_enabled),
      menu_dinamico_enabled: Boolean(client.menu_dinamico_enabled),
      menu_estatico_enabled: Boolean(client.menu_estatico_enabled),
      crm_ai_enabled: Boolean(client.crm_ai_enabled),
      // 0 = sin tope: `|| 20` lo habría convertido en 20 al abrir el cliente.
      crm_ai_daily_limit: client.crm_ai_daily_limit == null ? 20 : Number(client.crm_ai_daily_limit),
      crm_plan_tier: client.crm_plan_tier || 'emprendedor',
      crm_ai_api_key: '',
      crm_ai_base_url: client.crm_ai_base_url || DEFAULT_AI_PRESET.baseUrl,
      crm_ai_model: client.crm_ai_model || DEFAULT_AI_PRESET.model,
      crm_ai_transcription_model: client.crm_ai_transcription_model || '',
    });
    setAiProviderPreset(matchAiPreset(client.crm_ai_base_url || DEFAULT_AI_PRESET.baseUrl));
    setAiKeyTestResult(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingClient(null);
    setShowPassword(false);
    setAiKeyTestResult(null);
    setAiProviderPreset(DEFAULT_AI_PRESET.id);
    setRevealedAiKey(null);
    setNewClient({
      product_type: 'pos', name: '', email: '', phone: '', category: 'restaurant',
      plan: 'basico', status: 'trial', password: '',
      payment_due: '', extra_tables: 0, extra_products: 0, extra_employees: 0,
      crm_enabled: false, menu_dinamico_enabled: false, menu_estatico_enabled: false,
      crm_ai_enabled: false, crm_ai_daily_limit: 20, crm_plan_tier: '', crm_ai_api_key: '',
      crm_ai_base_url: DEFAULT_AI_PRESET.baseUrl, crm_ai_model: DEFAULT_AI_PRESET.model,
      crm_ai_transcription_model: '',
    });
  };

  const handleUpdateTicket = async (id, status, admin_notes) => {
    try {
      await api.put(`/api/tickets/${id}`, { status, admin_notes });
      loadTickets();
      setViewingTicket(prev => prev ? { ...prev, status, admin_notes } : null);
    } catch (error) {
      alert('Error al actualizar el ticket');
    }
  };

  const handleDeleteTicket = async (id) => {
    if (!window.confirm('¿Eliminar este ticket?')) return;
    try {
      await api.delete(`/api/tickets/${id}`);
      loadTickets();
      setViewingTicket(null);
    } catch (error) {
      alert('Error al eliminar el ticket');
    }
  };

  const generateRandomPassword = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let password = '';
    for (let i = 0; i < 10; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewClient({ ...newClient, password });
  };

  const filteredClients = clients.filter(client => {
    const matchesSearch = client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         client.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || client.category === filterCategory;
    const matchesStatus = filterStatus === 'all' || client.status === filterStatus;
    return matchesSearch && matchesCategory && matchesStatus;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'trial': return 'bg-blue-100 text-blue-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      case 'suspended': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return <CheckCircle className="w-4 h-4" />;
      case 'trial': return <Clock className="w-4 h-4" />;
      case 'cancelled': return <XCircle className="w-4 h-4" />;
      case 'suspended': return <AlertCircle className="w-4 h-4" />;
      default: return null;
    }
  };

  const getCategoryLabel = (category) => {
    const labels = {
      restaurant: 'Restaurante',
      pharmacy: 'Farmacia',
      retail: 'Tienda',
      services: 'Servicios'
    };
    return labels[category] || category;
  };

  const getPlanLabel = (plan) => {
    const labels = {
      basico: 'Básico',
      basic: 'Básico',
      estandar: 'Estándar',
      pro: 'Estándar',
      premium: 'Premium',
      gold: 'Premium',
      empresarial: 'Empresarial'
    };
    return labels[plan] || plan;
  };

  const getPlanBadgeColor = (plan) => {
    switch (plan) {
      case 'basico':
      case 'basic':
        return 'bg-gray-100 text-gray-800';
      case 'estandar':
      case 'pro':
        return 'bg-blue-100 text-blue-800';
      case 'premium':
      case 'gold':
        return 'bg-purple-100 text-purple-800';
      case 'empresarial':
        return 'bg-gradient-to-r from-yellow-400 to-orange-500 text-white';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // ⭐ BUGFIX (2026-08-07, reportado por Angelo: "le cambio el plan a una
  // cuenta CRM pero no se refleja en el dashboard admin"): la tabla de
  // clientes SIEMPRE pintaba el badge con `client.plan` (la columna de plan
  // de POS) y getPlanLabel/getPlanBadgeColor de arriba, sin importar
  // product_type -- el UPDATE de crm_plan_tier funcionaba bien en el backend
  // (COALESCE correcto, ver /api/update-client/:id) y el modal de editar sí
  // mostraba el select correcto, pero la fila de la tabla nunca leía esa
  // columna, así que una cuenta CRM-only siempre se veía con el plan de POS
  // por defecto ("Básico"). Estos 2 helpers son el equivalente para
  // crm_plan_tier, usados condicionalmente más abajo según product_type.
  const getCrmPlanLabel = (tier) => CRM_PLAN_TIERS.find(t => t.id === tier)?.label.split(' (')[0] || (tier || 'Emprendedor');

  const getCrmPlanBadgeColor = (tier) => {
    switch (tier) {
      case 'ia_plus':
        return 'bg-blue-100 text-blue-800';
      case 'business':
        return 'bg-gradient-to-r from-yellow-400 to-orange-500 text-white';
      case 'emprendedor':
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // ⭐ Cuenta CRM-only (pedido de Angelo, 2026-08-07): el modal de
  // crear/editar cliente es el mismo para las 2 familias de producto, pero
  // los campos de abajo (categoría de POS, plan de POS, límites extra de
  // mesas/productos/empleados) no significan nada para una cuenta que nunca
  // tuvo punto de venta -- de ahí el reporte de Angelo viendo "Categoría:
  // Restaurante" en una cuenta CRM (la categoría real que guarda el
  // registro standalone, ver /api/register-crm, no coincidía con NINGÚN
  // <option> del dropdown de abajo por un typo de datos ya corregido, así
  // que el <select> caía visualmente en el primer option de la lista).
  // Solo tiene sentido leer esto de `editingClient` (no de `newClient`):
  // el admin nunca CREA una cuenta CRM desde acá, solo edita las que ya se
  // registraron desde la landing.
  const isCrmClient = newClient.product_type === 'crm';
  const isServicesClient = newClient.product_type === 'services';

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-100 to-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* ⭐ Header (pedido de Angelo, 2026-08-07: "quiero que mejores la
            interfaz gráfica del administrador, la siento muy simple") --
            mismo contenido de antes (título + subtítulo), ahora con una
            banda con degradado en vez de texto plano sobre el fondo gris,
            para que el panel se sienta menos "genérico" al entrar. */}
        <div className="mb-8 bg-gradient-to-r from-slate-800 via-slate-800 to-blue-900 rounded-2xl shadow-lg px-8 py-7 text-white">
          <div className="flex items-center gap-4">
            <div className="bg-white/10 p-3 rounded-xl backdrop-blur-sm">
              <Sparkles className="w-7 h-7 text-blue-300" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Gestor SaaS POS</h1>
              <p className="text-slate-300 text-sm mt-0.5">Panel de administración central</p>
            </div>
          </div>
        </div>

        {/* Stats Cards -- mismos 4 números de siempre, ahora con acento de
            color a la izquierda y el ícono en un círculo con degradado en
            vez del círculo plano de un solo color. */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow p-6 border-l-4 border-blue-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1 font-medium">Total Clientes</p>
                <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
              </div>
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-3 rounded-xl shadow-sm">
                <Users className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow p-6 border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1 font-medium">Activos</p>
                <p className="text-3xl font-bold text-green-600">{stats.active}</p>
              </div>
              <div className="bg-gradient-to-br from-green-500 to-emerald-600 p-3 rounded-xl shadow-sm">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow p-6 border-l-4 border-sky-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1 font-medium">En Prueba</p>
                <p className="text-3xl font-bold text-blue-600">{stats.trial}</p>
              </div>
              <div className="bg-gradient-to-br from-sky-500 to-blue-600 p-3 rounded-xl shadow-sm">
                <Clock className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow p-6 border-l-4 border-red-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1 font-medium">Cancelados</p>
                <p className="text-3xl font-bold text-red-600">{stats.cancelled}</p>
              </div>
              <div className="bg-gradient-to-br from-red-500 to-rose-600 p-3 rounded-xl shadow-sm">
                <XCircle className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation -- mismos 3 tabs y mismo comportamiento, pestaña
            activa ahora con degradado + sombra de color en vez de un
            relleno plano, para que se note más cuál está seleccionada. */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('clients')}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm transition-all ${activeTab === 'clients' ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-md shadow-blue-200' : 'bg-white text-gray-600 hover:bg-gray-100 shadow-sm'}`}
          >
            <Users className="w-4 h-4" /> Clientes
          </button>
          <button
            onClick={() => { setActiveTab('tickets'); loadTickets(); }}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm transition-all ${activeTab === 'tickets' ? 'bg-gradient-to-r from-purple-600 to-purple-500 text-white shadow-md shadow-purple-200' : 'bg-white text-gray-600 hover:bg-gray-100 shadow-sm'}`}
          >
            <TicketCheck className="w-4 h-4" /> Tickets de Soporte
            {tickets.filter(t => t.status === 'open').length > 0 && (
              <span className="bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5 min-w-[18px] text-center">
                {tickets.filter(t => t.status === 'open').length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('metrics')}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm transition-all ${activeTab === 'metrics' ? 'bg-gradient-to-r from-green-600 to-emerald-500 text-white shadow-md shadow-green-200' : 'bg-white text-gray-600 hover:bg-gray-100 shadow-sm'}`}
          >
            <Activity className="w-4 h-4" /> Métricas
          </button>
          <button
            onClick={() => setActiveTab('agency')}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm transition-all ${activeTab === 'agency' ? 'bg-gradient-to-r from-indigo-600 to-blue-500 text-white shadow-md shadow-indigo-200' : 'bg-white text-gray-600 hover:bg-gray-100 shadow-sm'}`}
          >
            <BriefcaseBusiness className="w-4 h-4" /> Operación MARKETOS
          </button>
        </div>

        {activeTab === 'agency' && <AgencyOperations />}

        {activeTab === 'metrics' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <MetricsPanel />
          </div>
        )}

        {activeTab === 'tickets' && (
          <div>
            {/* Ticket filter */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-4 flex flex-wrap gap-2 items-center justify-between">
              <div className="flex gap-2 flex-wrap">
                {['all','open','in_progress','resolved','closed'].map(s => (
                  <button key={s}
                    onClick={() => setTicketFilter(s)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${ticketFilter === s ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                  >
                    {s === 'all' && 'Todos'}
                    {s === 'open' && `🔴 Abiertos (${tickets.filter(t => t.status === 'open').length})`}
                    {s === 'in_progress' && `🟡 En proceso (${tickets.filter(t => t.status === 'in_progress').length})`}
                    {s === 'resolved' && `🟢 Resueltos`}
                    {s === 'closed' && `⚫ Cerrados`}
                  </button>
                ))}
              </div>
              <button onClick={loadTickets} className="text-xs text-blue-600 hover:underline">↻ Actualizar</button>
            </div>

            {/* Tickets list */}
            <div className="space-y-3">
              {tickets.filter(t => ticketFilter === 'all' || t.status === ticketFilter).length === 0 ? (
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center text-gray-400">
                  <TicketCheck className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No hay tickets {ticketFilter !== 'all' ? 'con este estado' : 'aún'}</p>
                </div>
              ) : tickets.filter(t => ticketFilter === 'all' || t.status === ticketFilter).map(ticket => (
                <div key={ticket.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => { setViewingTicket(ticket); setTicketAdminNotes(ticket.admin_notes || ''); }}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                          ticket.status === 'open' ? 'bg-red-100 text-red-700' :
                          ticket.status === 'in_progress' ? 'bg-yellow-100 text-yellow-700' :
                          ticket.status === 'resolved' ? 'bg-green-100 text-green-700' :
                          'bg-gray-100 text-gray-600'}`}>
                          {ticket.status === 'open' && '🔴 Abierto'}
                          {ticket.status === 'in_progress' && '🟡 En proceso'}
                          {ticket.status === 'resolved' && '🟢 Resuelto'}
                          {ticket.status === 'closed' && '⚫ Cerrado'}
                        </span>
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                          ticket.priority === 'urgent' ? 'bg-red-200 text-red-800' :
                          ticket.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                          ticket.priority === 'medium' ? 'bg-blue-100 text-blue-700' :
                          'bg-gray-100 text-gray-500'}`}>
                          {ticket.priority === 'urgent' && '🚨 Urgente'}
                          {ticket.priority === 'high' && '⚡ Alta'}
                          {ticket.priority === 'medium' && '🔵 Media'}
                          {ticket.priority === 'low' && '⬇️ Baja'}
                        </span>
                        <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                          {ticket.type === 'bug' && '🐛 Bug'}{ticket.type === 'request' && '✨ Solicitud'}{ticket.type === 'question' && '❓ Pregunta'}{ticket.type === 'other' && '📌 Otro'}
                        </span>
                      </div>
                      <p className="font-semibold text-gray-900 truncate">{ticket.subject}</p>
                      <p className="text-sm text-gray-500 truncate mt-0.5">{ticket.description}</p>
                      <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                        <span>👤 {ticket.client_name}</span>
                        <span>✉️ {ticket.client_email}</span>
                        <span>📅 {new Date(ticket.created_at).toLocaleDateString('es-ES')}</span>
                      </div>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <button onClick={(e) => { e.stopPropagation(); handleDeleteTicket(ticket.id); }}
                        className="p-1.5 text-red-400 hover:text-red-700 hover:bg-red-50 rounded transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'clients' && (
        <>{/* Filters and Actions */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="flex flex-col md:flex-row gap-4 flex-1 w-full">
              <input
                type="text"
                placeholder="Buscar por nombre o email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">Todas las categorías</option>
                <option value="restaurant">Restaurantes</option>
                <option value="pharmacy">Farmacias</option>
                <option value="retail">Tiendas</option>
                <option value="services">Servicios</option>
                <option value="services">Servicios</option>
              </select>

              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">Todos los estados</option>
                <option value="active">Activos</option>
                <option value="trial">En Prueba</option>
                <option value="cancelled">Cancelados</option>
                <option value="suspended">Suspendidos</option>
              </select>
            </div>

            <button
              onClick={() => setShowModal(true)}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors font-semibold whitespace-nowrap"
            >
              + Nuevo Cliente
            </button>
          </div>
        </div>

        {/* Clients Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cliente
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Categoría
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Plan
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fecha de Creación
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredClients.map((client) => (
                  <tr key={client.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                          <span className="text-white font-semibold text-lg">
                            {client.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{client.name}</div>
                          <div className="text-sm text-gray-500">{client.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">{getCategoryLabel(client.category)}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {client.product_type === 'crm' ? (
                        <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${getCrmPlanBadgeColor(client.crm_plan_tier)}`}>
                          {getCrmPlanLabel(client.crm_plan_tier)}
                        </span>
                      ) : client.product_type === 'services' ? (
                        <span className="inline-flex rounded-full bg-sky-100 px-2.5 py-0.5 text-xs font-semibold text-sky-800">
                          Servicios MARKETOS
                        </span>
                      ) : (
                        <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${getPlanBadgeColor(client.plan)}`}>
                          {getPlanLabel(client.plan)}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(client.status)}`}>
                        {getStatusIcon(client.status)}
                        {client.status === 'active' && 'Activo'}
                        {client.status === 'trial' && 'Prueba'}
                        {client.status === 'cancelled' && 'Cancelado'}
                        {client.status === 'suspended' && 'Suspendido'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(client.created_at).toLocaleDateString('es-ES')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleResetPassword(client)}
                          className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded transition-colors"
                          title="Restablecer acceso"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openEditModal(client)}
                          className="text-yellow-600 hover:text-yellow-900 p-1 hover:bg-yellow-50 rounded transition-colors"
                          title="Editar"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteClient(client.id)}
                          className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 rounded transition-colors"
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        
                        {/* Status change buttons */}
                        {client.status === 'trial' && (
                          <button
                            onClick={() => handleUpdateStatus(client.id, 'active')}
                            className="ml-2 px-3 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 text-xs font-medium transition-colors"
                          >
                            Activar
                          </button>
                        )}
                        {client.status === 'active' && (
                          <button
                            onClick={() => handleUpdateStatus(client.id, 'cancelled')}
                            className="ml-2 px-3 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 text-xs font-medium transition-colors"
                          >
                            Cancelar
                          </button>
                        )}
                        {client.status === 'cancelled' && (
                          <button
                            onClick={() => handleUpdateStatus(client.id, 'active')}
                            className="ml-2 px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 text-xs font-medium transition-colors"
                          >
                            Reactivar
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredClients.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">No se encontraron clientes</p>
            </div>
          )}
        </div>
        </>)}

      {/* Modal: Create/Edit Client */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-6 text-gray-900">
              {editingClient ? 'Editar Cliente' : 'Nuevo Cliente'}
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre del Negocio *
                </label>
                <input
                  type="text"
                  value={newClient.name}
                  onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ej: Restaurante El Buen Sabor"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  value={newClient.email}
                  onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="contacto@negocio.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Teléfono
                </label>
                <input
                  type="tel"
                  value={newClient.phone}
                  onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0999999999"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de acceso</label>
                <select
                  value={newClient.product_type}
                  onChange={(e) => {
                    const type = e.target.value;
                    setNewClient({
                      ...newClient,
                      product_type: type,
                      category: type === 'services' ? 'services' : newClient.category,
                      plan: type === 'services' ? 'basico' : newClient.plan,
                      status: type === 'services' ? 'active' : newClient.status
                    });
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="pos">Punto de Venta</option>
                  <option value="services">Servicios MARKETOS</option>
                  {isCrmClient && <option value="crm">CRM</option>}
                </select>
                {isServicesClient && <p className="mt-1 text-xs text-sky-700">El cliente verá únicamente su portal de servicios.</p>}
              </div>

              {isCrmClient ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Categoría
                  </label>
                  <div className="w-full px-4 py-2 border border-gray-200 bg-gray-50 rounded-lg text-sm font-semibold text-gray-500">
                    💬 Cuenta CRM (sin punto de venta)
                  </div>
                </div>
              ) : isServicesClient ? (
                <div className="rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 text-sm font-semibold text-sky-800">
                  Cuenta de servicios digitales, sin punto de venta.
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Categoría
                  </label>
                  <select
                    value={newClient.category}
                    onChange={(e) => setNewClient({ ...newClient, category: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="restaurant">Restaurante</option>
                    <option value="pharmacy">Farmacia</option>
                    <option value="retail">Tienda</option>
                    <option value="services">Servicios</option>
                  </select>
                </div>
              )}

              {isCrmClient ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Plan CRM
                  </label>
                  <select
                    value={newClient.crm_plan_tier}
                    onChange={(e) => setNewClient({ ...newClient, crm_plan_tier: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {CRM_PLAN_TIERS.map(t => (
                      <option key={t.id} value={t.id}>{t.label}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Mismos 3 planes de la landing (/crm) -- IA Plus y Business todavía no tienen checkout propio, así que subir a un cliente a esos tiers acá es manual hasta que ese flujo exista.
                  </p>
                </div>
              ) : isServicesClient ? (
                <div className="rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
                  Los productos y avances se administran desde Operación MARKETOS.
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Plan
                  </label>
                  <select
                    value={newClient.plan}
                    onChange={(e) => setNewClient({ ...newClient, plan: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {getVertical(newClient.category) === 'tienda' ? (
                      <>
                        <option value="basico">Básico ($15/mes) - 250 productos</option>
                        <option value="estandar">Estándar ($25/mes) - 500 productos</option>
                        <option value="premium">Premium ($39/mes) - Productos ilimitados</option>
                        <option value="empresarial">Empresarial ($79/mes) - Todo ilimitado</option>
                      </>
                    ) : (
                      <>
                        <option value="basico">Básico ($19/mes) - 8 mesas, 100 productos</option>
                        <option value="estandar">Estándar ($29/mes) - 15 mesas, 500 productos, comandas</option>
                        <option value="premium">Premium ($49/mes) - 30 mesas, productos ilimitados, comandas</option>
                        <option value="empresarial">Empresarial ($89/mes) - Todo ilimitado</option>
                      </>
                    )}
                  </select>

                  {/* Info del plan seleccionado — estos textos deben coincidir SIEMPRE
                      con getPlanLimits() en backend/index.js, que es la fuente de verdad
                      real que se aplica al crear mesas/productos/empleados. Restaurantes
                      y tiendas tienen precio y límite de productos distintos desde
                      2026-08-04 (ver análisis de pricing entregado a Angelo). */}
                  {getVertical(newClient.category) === 'tienda' ? (
                    <div className="mt-2 text-xs text-gray-600 bg-gray-50 p-2 rounded">
                      {newClient.plan === 'basico' && '✅ 250 productos • 10 categorías • 3 empleados'}
                      {newClient.plan === 'estandar' && '✅ 500 productos • 30 categorías • 8 empleados'}
                      {newClient.plan === 'premium' && '✅ Productos ilimitados • 100 categorías • 20 empleados'}
                      {newClient.plan === 'empresarial' && '✅ Todo ilimitado • Multi-sucursal • API'}
                    </div>
                  ) : (
                    <div className="mt-2 text-xs text-gray-600 bg-gray-50 p-2 rounded">
                      {newClient.plan === 'basico' && '✅ 8 mesas • 100 productos • 10 categorías • 3 empleados'}
                      {newClient.plan === 'estandar' && '✅ 15 mesas • 500 productos • 30 categorías • 8 empleados • Comandas'}
                      {newClient.plan === 'premium' && '✅ 30 mesas • Productos ilimitados • 100 categorías • 20 empleados • Comandas'}
                      {newClient.plan === 'empresarial' && '✅ Todo ilimitado • Multi-sucursal • API'}
                    </div>
                  )}
                </div>
              )}

              {!editingClient && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contraseña * (mínimo 6 caracteres)
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={newClient.password}
                      onChange={(e) => setNewClient({ ...newClient, password: e.target.value })}
                      className="w-full px-4 py-2 pr-20 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Contraseña fácil de recordar"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={generateRandomPassword}
                    className="mt-2 text-xs text-blue-600 hover:underline"
                  >
                    🎲 Generar contraseña aleatoria
                  </button>
                </div>
              )}

              {editingClient && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Estado</label>
                    <select
                      value={newClient.status}
                      onChange={(e) => setNewClient({ ...newClient, status: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="trial">Prueba</option>
                      <option value="active">Activo</option>
                      <option value="suspended">Suspendido</option>
                      <option value="cancelled">Cancelado</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      📅 Fecha de vencimiento del plan
                    </label>
                    <input
                      type="date"
                      value={newClient.payment_due}
                      onChange={(e) => setNewClient({ ...newClient, payment_due: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Fecha en que expira el trial o el plan activo</p>
                  </div>

                  {!isCrmClient && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm font-semibold text-blue-800 mb-3">🔧 Límites extra (sobre el plan base)</p>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">🪑 Mesas extra</label>
                        <input
                          type="number" min="0"
                          value={newClient.extra_tables}
                          onChange={(e) => setNewClient({ ...newClient, extra_tables: parseInt(e.target.value) || 0 })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">📦 Productos extra</label>
                        <input
                          type="number" min="0"
                          value={newClient.extra_products}
                          onChange={(e) => setNewClient({ ...newClient, extra_products: parseInt(e.target.value) || 0 })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">👥 Empleados extra</label>
                        <input
                          type="number" min="0"
                          value={newClient.extra_employees}
                          onChange={(e) => setNewClient({ ...newClient, extra_employees: parseInt(e.target.value) || 0 })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">Estos se suman al límite del plan. Ej: Básico (8 mesas) + 2 extra = 10 mesas total</p>
                  </div>
                  )}

                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newClient.crm_enabled}
                        onChange={(e) => setNewClient({ ...newClient, crm_enabled: e.target.checked })}
                        className="mt-0.5 w-4 h-4"
                      />
                      <span>
                        <span className="block text-sm font-semibold text-emerald-800">💬 Addon CRM / Conversaciones activado</span>
                        <span className="block text-xs text-gray-600 mt-0.5">
                          Actívalo para clientes Básico/Estándar que pagan el addon aparte. En Premium/Empresarial con status Activo el CRM ya viene incluido automáticamente, sin necesidad de marcar esto.
                        </span>
                      </span>
                    </label>

                    {newClient.crm_enabled && (
                      <div className="mt-3 pt-3 border-t border-emerald-200">
                        <label className="flex items-start gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={newClient.crm_ai_enabled}
                            onChange={(e) => setNewClient({ ...newClient, crm_ai_enabled: e.target.checked })}
                            className="mt-0.5 w-4 h-4"
                          />
                          <span>
                            <span className="block text-sm font-semibold text-emerald-800">🤖 Respuesta automática por IA</span>
                            <span className="block text-xs text-gray-600 mt-0.5">
                              Reservado para la cuenta de demo comercial -- la IA contesta sola por WhatsApp para mostrarle el sistema a prospectos. No activar en cuentas de clientes reales todavía.
                            </span>
                          </span>
                        </label>
                        {newClient.crm_ai_enabled && (
                          <div className="mt-2 pl-6">
                            {/* ⭐ Tope diario, con opción "sin tope" (pedido de
                                Angelo, 2026-08-10). Se guarda como 0 en
                                crm_ai_daily_limit -- ver resolveDailyLimit en
                                backend/crm/aiReply.js. */}
                            <label className="block text-xs font-medium text-gray-600 mb-1">Tope de mensajes IA por día</label>
                            <label className="flex items-center gap-2 mb-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={Number(newClient.crm_ai_daily_limit) === 0}
                                onChange={(e) => setNewClient({ ...newClient, crm_ai_daily_limit: e.target.checked ? 0 : 20 })}
                                className="rounded"
                              />
                              <span className="text-sm text-gray-700">Sin tope (ilimitado)</span>
                            </label>
                            {Number(newClient.crm_ai_daily_limit) !== 0 && (
                              <input
                                type="number" min="1"
                                value={newClient.crm_ai_daily_limit}
                                onChange={(e) => setNewClient({ ...newClient, crm_ai_daily_limit: parseInt(e.target.value) || 20 })}
                                className="w-32 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                              />
                            )}
                            <p className="text-xs text-gray-500 mt-1">
                              Cuenta respuestas de IA, clasificación de intención y resúmenes de notificación interna.
                            </p>

                            <label className="block text-xs font-medium text-gray-600 mb-1 mt-3">
                              Proveedor de IA
                            </label>
                            <select
                              value={aiProviderPreset}
                              onChange={(e) => handleAiPresetChange(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white"
                            >
                              {AI_PROVIDER_PRESETS.map((p) => (
                                <option key={p.id} value={p.id}>{p.label}</option>
                              ))}
                            </select>
                            <p className="text-xs text-gray-500 mt-1">
                              Cualquier proveedor compatible con el formato de OpenAI funciona -- solo cambia estos 3 campos, como un nodo de n8n. No usa Google AI Studio.
                            </p>

                            <div className="grid grid-cols-2 gap-2 mt-2">
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">URL base</label>
                                <input
                                  type="text"
                                  value={newClient.crm_ai_base_url}
                                  onChange={(e) => {
                                    setNewClient({ ...newClient, crm_ai_base_url: e.target.value });
                                    setAiKeyTestResult(null);
                                  }}
                                  placeholder="https://api.deepseek.com/v1"
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Modelo</label>
                                <input
                                  type="text"
                                  value={newClient.crm_ai_model}
                                  onChange={(e) => {
                                    setNewClient({ ...newClient, crm_ai_model: e.target.value });
                                    setAiKeyTestResult(null);
                                  }}
                                  placeholder="deepseek-chat"
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                />
                              </div>
                            </div>

                            {/* ⭐ Nodo IA: transcribir audio (pedido de Angelo,
                                2026-08-07) -- modelo aparte del de chat de
                                arriba, ver comentario en el estado inicial. */}
                            <label className="block text-xs font-medium text-gray-600 mb-1 mt-3">
                              Modelo de transcripción de audio (opcional)
                            </label>
                            <input
                              type="text"
                              value={newClient.crm_ai_transcription_model}
                              onChange={(e) => setNewClient({ ...newClient, crm_ai_transcription_model: e.target.value })}
                              placeholder="whisper-1 (default si se deja vacío)"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Usado por el nodo "Responder con IA" cuando el último mensaje del contacto es un audio. No todo proveedor de chat compatible con OpenAI tiene endpoint de transcripción (ej. OpenAI y Groq sí, otros no) -- si el proveedor elegido arriba no lo soporta, la transcripción falla con un error claro, pero el resto de la IA sigue funcionando.
                            </p>

                            <label className="block text-xs font-medium text-gray-600 mb-1 mt-3">
                              API Key del proveedor
                            </label>
                            <div className="flex gap-2">
                              <input
                                type="password"
                                value={newClient.crm_ai_api_key}
                                onChange={(e) => {
                                  setNewClient({ ...newClient, crm_ai_api_key: e.target.value });
                                  setAiKeyTestResult(null);
                                }}
                                placeholder="Dejar vacío para usar la clave del servidor"
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                autoComplete="new-password"
                              />
                              <button
                                type="button"
                                onClick={handleTestAiKey}
                                disabled={!newClient.crm_ai_api_key?.trim() || testingAiKey}
                                className="px-3 py-2 text-sm font-medium rounded-lg border border-emerald-300 text-emerald-700 hover:bg-emerald-50 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                              >
                                {testingAiKey ? 'Probando...' : 'Probar conexión'}
                              </button>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              Si la dejas vacía, se sigue usando la clave configurada en el servidor (AI_API_KEY). Si escribes una clave nueva, esta cuenta usará esa clave, URL y modelo en su lugar (no hace falta tocar Render).
                            </p>
                            {/* ⭐ Ver clave ya guardada (pedido de Angelo, 2026-08-07):
                                solo tiene sentido editando una cuenta existente -- al
                                crear una cuenta nueva todavía no hay nada guardado. */}
                            {editingClient && (
                              <div className="mt-2">
                                <button
                                  type="button"
                                  onClick={handleViewAiKey}
                                  disabled={loadingAiKeyReveal}
                                  className="text-xs font-medium text-emerald-700 hover:text-emerald-900 underline disabled:opacity-40"
                                >
                                  {loadingAiKeyReveal ? 'Cargando...' : 'Ver clave guardada'}
                                </button>
                                {revealedAiKey === '__error__' && (
                                  <p className="text-xs font-medium text-red-600 mt-1">❌ No se pudo obtener la clave guardada.</p>
                                )}
                                {revealedAiKey === '__none__' && (
                                  <p className="text-xs text-gray-500 mt-1">Esta cuenta no tiene clave propia guardada -- usa la clave del servidor (AI_API_KEY).</p>
                                )}
                                {revealedAiKey !== null && revealedAiKey !== '__error__' && revealedAiKey !== '__none__' && (
                                  <p className="text-xs font-mono text-gray-700 bg-gray-100 border border-gray-300 rounded px-2 py-1 mt-1 break-all select-all">
                                    {revealedAiKey}
                                  </p>
                                )}
                              </div>
                            )}
                            {aiKeyTestResult && (
                              aiKeyTestResult.ok ? (
                                <p className="text-xs font-medium text-emerald-700 mt-1">✅ La conexión funciona correctamente.</p>
                              ) : (
                                <p className="text-xs font-medium text-red-600 mt-1">❌ {aiKeyTestResult.error}</p>
                              )
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newClient.menu_dinamico_enabled}
                        onChange={(e) => setNewClient({ ...newClient, menu_dinamico_enabled: e.target.checked })}
                        className="mt-0.5 w-4 h-4"
                      />
                      <span>
                        <span className="block text-sm font-semibold text-indigo-800">🌐 Addon Páginas Web (dinámicas) activado</span>
                        <span className="block text-xs text-gray-600 mt-0.5">
                          Habilita el constructor visual de páginas web: landings editables, formularios que crean contactos en el CRM y publicación en /p/tu-sitio.
                          Addon puro, sin prueba gratuita ni inclusión por plan -- actívalo solo cuando el cliente ya pagó por él. Sin esto marcado, la pestaña ni aparece en su panel.
                        </span>
                      </span>
                    </label>
                  </div>

                  {/* ⭐ Gemelo del anterior para el Menú Web Estático (2026-08-08).
                      Antes el estático solo se desbloqueaba por plan Premium, así que
                      no había forma de vendérselo a una cuenta de plan menor ni a una
                      cuenta CRM-only (que no tiene plan de POS). Este switch es
                      aditivo: con Premium sigue viniendo incluido igual. */}
                  <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mt-3">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newClient.menu_estatico_enabled}
                        onChange={(e) => setNewClient({ ...newClient, menu_estatico_enabled: e.target.checked })}
                        className="mt-0.5 w-4 h-4"
                      />
                      <span>
                        <span className="block text-sm font-semibold text-indigo-800">📖 Addon Menú Web Estático activado</span>
                        <span className="block text-xs text-gray-600 mt-0.5">
                          La carta digital con plantilla y QR. Ya viene incluida con el plan Premium o superior:
                          marca esto solo para dársela a una cuenta de plan menor o a una cuenta CRM-only.
                        </span>
                      </span>
                    </label>
                  </div>
                </>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={closeModal}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={handleCreateClient}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                {editingClient ? 'Actualizar' : 'Crear Cliente'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Detalle de Ticket */}
      {viewingTicket && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Ticket #{viewingTicket.id}</h2>
              <button onClick={() => setViewingTicket(null)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
            </div>

            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-xs text-gray-500 mb-0.5">Cliente</p>
                <p className="font-semibold text-gray-900">{viewingTicket.client_name}</p>
                <p className="text-sm text-gray-500">{viewingTicket.client_email}</p>
              </div>

              <div>
                <p className="text-xs text-gray-500 mb-0.5">Asunto</p>
                <p className="font-semibold text-gray-900">{viewingTicket.subject}</p>
              </div>

              <div>
                <p className="text-xs text-gray-500 mb-0.5">Descripción</p>
                <p className="text-gray-800 whitespace-pre-wrap bg-gray-50 p-3 rounded-lg text-sm">{viewingTicket.description}</p>
              </div>

              <div className="flex gap-2 flex-wrap">
                <span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${viewingTicket.priority === 'urgent' ? 'bg-red-200 text-red-800' : viewingTicket.priority === 'high' ? 'bg-orange-100 text-orange-700' : viewingTicket.priority === 'medium' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
                  Prioridad: {viewingTicket.priority}
                </span>
                <span className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                  Tipo: {viewingTicket.type}
                </span>
                <span className="text-xs text-gray-400">📅 {new Date(viewingTicket.created_at).toLocaleString('es-ES')}</span>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Cambiar estado</label>
                <div className="flex gap-2 flex-wrap">
                  {['open','in_progress','resolved','closed'].map(s => (
                    <button key={s}
                      onClick={() => handleUpdateTicket(viewingTicket.id, s, ticketAdminNotes)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${viewingTicket.status === s ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
                      {s === 'open' && '🔴 Abierto'}
                      {s === 'in_progress' && '🟡 En proceso'}
                      {s === 'resolved' && '🟢 Resuelto'}
                      {s === 'closed' && '⚫ Cerrado'}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">📝 Notas del administrador</label>
                <textarea
                  value={ticketAdminNotes}
                  onChange={(e) => setTicketAdminNotes(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Escribe notas internas sobre este ticket..."
                />
                <button
                  onClick={() => handleUpdateTicket(viewingTicket.id, viewingTicket.status, ticketAdminNotes)}
                  className="mt-2 px-4 py-1.5 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 transition-colors"
                >
                  Guardar notas
                </button>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={() => setViewingTicket(null)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium">
                Cerrar
              </button>
              <button onClick={() => handleDeleteTicket(viewingTicket.id)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium">
                Eliminar ticket
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

export default Dashboard;
